import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Brand } from '@tips-crm/shared';
import {
  createBrand,
  deleteBrand,
  fetchBrands,
  fetchSellers,
  updateBrand,
} from '../../api/endpoints.js';
import { errorMessage } from '../../api/client.js';
import { PageHeader } from '../../components/PageHeader.js';
import { DataTable, type Column } from '../../components/DataTable.js';
import { Modal } from '../../components/Modal.js';

interface BrandForm {
  seller_id: string;
  name: string;
  logo_url: string;
  primary_color: string;
  support_display_name: string;
  privacy_url: string;
  welcome_copy: string;
}

const EMPTY: BrandForm = {
  seller_id: '',
  name: '',
  logo_url: '',
  primary_color: '',
  support_display_name: '',
  privacy_url: '',
  welcome_copy: '',
};

export function BrandsPage() {
  const qc = useQueryClient();
  const [sellerFilter, setSellerFilter] = useState('');
  const [editing, setEditing] = useState<Brand | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<BrandForm>(EMPTY);

  const sellersQ = useQuery({ queryKey: ['sellers'], queryFn: fetchSellers });
  const brandsQ = useQuery({
    queryKey: ['brands', sellerFilter],
    queryFn: () => fetchBrands(sellerFilter || undefined),
  });

  const sellerName = (id: string) =>
    sellersQ.data?.find((s) => s.id === id)?.name ?? id;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        seller_id: form.seller_id,
        name: form.name,
        logo_url: form.logo_url || null,
        primary_color: form.primary_color || null,
        support_display_name: form.support_display_name || null,
        privacy_url: form.privacy_url || null,
        welcome_copy: form.welcome_copy || null,
      };
      return editing ? updateBrand(editing.id, payload) : createBrand(payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['brands'] });
      setOpen(false);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteBrand(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brands'] }),
  });

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY, seller_id: sellerFilter || sellersQ.data?.[0]?.id || '' });
    setOpen(true);
  }

  function openEdit(b: Brand) {
    setEditing(b);
    setForm({
      seller_id: b.seller_id,
      name: b.name,
      logo_url: b.logo_url ?? '',
      primary_color: b.primary_color ?? '',
      support_display_name: b.support_display_name ?? '',
      privacy_url: b.privacy_url ?? '',
      welcome_copy: b.welcome_copy ?? '',
    });
    setOpen(true);
  }

  const columns = useMemo<Column<Brand>[]>(
    () => [
      { key: 'name', header: 'Name', render: (b) => b.name },
      { key: 'seller', header: 'Seller', render: (b) => sellerName(b.seller_id) },
      {
        key: 'color',
        header: 'Color',
        render: (b) =>
          b.primary_color ? (
            <span className="swatch" style={{ background: b.primary_color }} title={b.primary_color} />
          ) : (
            '—'
          ),
      },
      { key: 'support', header: 'Support name', render: (b) => b.support_display_name ?? '—' },
      {
        key: 'actions',
        header: '',
        render: (b) => (
          <div className="row-actions" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => openEdit(b)}>
              Edit
            </button>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              onClick={() => {
                if (confirm(`Delete brand "${b.name}"?`)) deleteMutation.mutate(b.id);
              }}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteMutation, sellersQ.data],
  );

  function set<K extends keyof BrandForm>(key: K, value: BrandForm[K]) {
    setForm((p) => ({ ...p, [key]: value }));
  }

  return (
    <div className="page">
      <PageHeader
        title="Brands"
        subtitle="Branding applied to widgets and conversations."
        actions={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            New brand
          </button>
        }
      />

      <div className="toolbar">
        <select
          className="field__input"
          value={sellerFilter}
          onChange={(e) => setSellerFilter(e.target.value)}
        >
          <option value="">All sellers</option>
          {sellersQ.data?.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {deleteMutation.isError && <div className="form-error">{errorMessage(deleteMutation.error)}</div>}

      <DataTable
        columns={columns}
        rows={brandsQ.data}
        rowKey={(b) => b.id}
        isLoading={brandsQ.isLoading}
        isError={brandsQ.isError}
        error={brandsQ.error}
        onRetry={() => brandsQ.refetch()}
        emptyMessage="No brands yet."
      />

      <Modal open={open} title={editing ? 'Edit brand' : 'New brand'} onClose={() => setOpen(false)}>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            saveMutation.mutate();
          }}
        >
          <label className="field">
            <span className="field__label">Seller</span>
            <select
              className="field__input"
              value={form.seller_id}
              onChange={(e) => set('seller_id', e.target.value)}
              required
              disabled={Boolean(editing)}
            >
              <option value="">Select…</option>
              {sellersQ.data?.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Name</span>
            <input className="field__input" value={form.name} onChange={(e) => set('name', e.target.value)} required />
          </label>
          <label className="field">
            <span className="field__label">Logo URL</span>
            <input className="field__input" value={form.logo_url} onChange={(e) => set('logo_url', e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Primary color</span>
            <input
              className="field__input"
              type="text"
              placeholder="#1f6feb"
              value={form.primary_color}
              onChange={(e) => set('primary_color', e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">Support display name</span>
            <input
              className="field__input"
              value={form.support_display_name}
              onChange={(e) => set('support_display_name', e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">Privacy URL</span>
            <input className="field__input" value={form.privacy_url} onChange={(e) => set('privacy_url', e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Welcome copy</span>
            <textarea
              className="field__input"
              rows={2}
              value={form.welcome_copy}
              onChange={(e) => set('welcome_copy', e.target.value)}
            />
          </label>

          {saveMutation.isError && <div className="form-error">{errorMessage(saveMutation.error)}</div>}

          <div className="form__actions">
            <button type="button" className="btn btn--ghost" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={saveMutation.isPending || !form.name || !form.seller_id}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
